import React from "react";
import {
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  LinearProgress,
} from "@mui/material";
import { AccessTime, Warning, ErrorOutline } from "@mui/icons-material";
import { getConfig } from "../config/runtimeConfig";

export type SessionModalState = "warning" | "expired";

interface SessionTimeoutWarningModalProps {
  open: boolean;
  remainingSeconds: number;
  modalState: SessionModalState;
  onStayLoggedIn?: () => void;
  onLogout: () => void;
}

/**
 * Unified modal dialog that handles both session timeout warnings and expired sessions
 * - Warning state: Shows countdown with "Stay Logged In" and "Logout" options
 * - Expired state: Shows session expired message with only "Login Again" option
 */
const SessionTimeoutWarningModal: React.FC<SessionTimeoutWarningModalProps> = ({
  open,
  remainingSeconds,
  modalState,
  onStayLoggedIn,
  onLogout,
}) => {
  // Format time as MM:SS
  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  // Get warning threshold from environment variable
  const warningThresholdMinutes = parseFloat(
    getConfig("REACT_APP_SESSION_WARNING_THRESHOLD", "1")
  );
  const totalWarningTime = warningThresholdMinutes * 60;

  // Calculate progress percentage based on actual warning time
  const progressPercentage =
    modalState === "warning"
      ? ((totalWarningTime - remainingSeconds) / totalWarningTime) * 100
      : 100; // Full progress for expired state

  // Hide "Stay Logged In" button if more than 1 minute has passed since modal appeared
  const stayLoggedInTimeLimit = 60; // 1 minute in seconds
  const showStayLoggedInButton =
    modalState === "warning" &&
    remainingSeconds > totalWarningTime - stayLoggedInTimeLimit;

  // Determine color based on remaining time and modal state
  const getTimeColor = (seconds: number) => {
    if (modalState === "expired") return "error.main";
    if (seconds <= 15) return "error.main";
    if (seconds <= 30) return "warning.main";
    return "#ac9c9c";
  };

  const getProgressColor = (seconds: number) => {
    if (modalState === "expired") return "error";
    if (seconds <= 15) return "error";
    if (seconds <= 30) return "warning";
    return "primary";
  };

  // Get appropriate icon based on modal state
  const getIcon = () => {
    if (modalState === "expired") {
      return (
        <ErrorOutline
          sx={{
            fontSize: 60,
            color: "error.main",
            opacity: 0.8,
          }}
        />
      );
    }

    return (
      <Box
        sx={{
          position: "relative",
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <AccessTime
          sx={{
            fontSize: 60,
            color: getTimeColor(remainingSeconds),
            opacity: 0.8,
          }}
        />
        <Warning
          sx={{
            fontSize: 24,
            color: "warning.main",
            position: "absolute",
            bottom: -2,
            right: -2,
            backgroundColor: "white",
            borderRadius: "50%",
            padding: "2px",
          }}
        />
      </Box>
    );
  };

  return (
    <Dialog
      open={open}
      disableEscapeKeyDown
      onClose={(_event, reason) => {
        // Prevent closing by clicking outside or pressing escape
        if (reason !== "backdropClick") {
          return;
        }
      }}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          padding: 2,
        },
      }}
    >
      <DialogContent sx={{ textAlign: "center", py: 4 }}>
        <Box sx={{ mb: 3 }}>
          {/* Dynamic icon based on modal state */}
          <Box sx={{ mb: 2 }}>{getIcon()}</Box>
        </Box>

        <Typography
          variant="h5"
          sx={{
            fontWeight: 600,
            mb: 2,
            color: "text.primary",
          }}
        >
          {modalState === "expired"
            ? "Your session has expired"
            : "Session Timeout Warning"}
        </Typography>

        {modalState === "warning" ? (
          <>
            <Typography
              variant="body1"
              sx={{
                color: "text.secondary",
                mb: 2,
              }}
            >
              Your session will expire in {formatTime(remainingSeconds)} due to
              inactivity.
            </Typography>

            {/* Countdown Timer */}
            <Typography
              variant="h2"
              sx={{
                color: getTimeColor(remainingSeconds),
                fontWeight: "bold",
                mb: 2,
                fontFamily: "monospace",
              }}
            >
              {formatTime(remainingSeconds)}
            </Typography>

            {/* Progress Bar */}
            <Box sx={{ mb: 3 }}>
              <LinearProgress
                variant="determinate"
                value={progressPercentage}
                color={getProgressColor(remainingSeconds)}
                sx={{
                  height: 8,
                  borderRadius: 4,
                  backgroundColor: "grey.200",
                }}
              />
            </Box>

            <Typography
              variant="body1"
              sx={{
                color: "text.secondary",
                mb: 1,
              }}
            >
              {showStayLoggedInButton
                ? "Click 'Stay Logged In' to continue or 'Logout' to end session."
                : "Session timeout exceeded. Please logout and login again."}
            </Typography>
          </>
        ) : (
          <>
            <Typography
              variant="body1"
              sx={{
                color: "text.secondary",
                mb: 1,
              }}
            >
              For your security, you have been logged out due to inactivity.
            </Typography>

            <Typography
              variant="body2"
              sx={{
                color: "text.secondary",
              }}
            >
              Please log in again to continue.
            </Typography>
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ justifyContent: "center", gap: 2, pb: 3 }}>
        {modalState === "warning" ? (
          <>
            <Button
              variant="outlined"
              color="error"
              size="large"
              onClick={onLogout}
              sx={{
                minWidth: 140,
                py: 1.5,
                borderRadius: 2,
                textTransform: "none",
                fontSize: "1rem",
                fontWeight: 600,
              }}
            >
              Logout
            </Button>
            {onStayLoggedIn && showStayLoggedInButton && (
              <Button
                variant="contained"
                color="primary"
                size="large"
                onClick={onStayLoggedIn}
                sx={{
                  minWidth: 140,
                  py: 1.5,
                  borderRadius: 2,
                  textTransform: "none",
                  fontSize: "1rem",
                  fontWeight: 600,
                }}
              >
                Stay Logged In
              </Button>
            )}
          </>
        ) : (
          <Button
            variant="contained"
            color="primary"
            size="large"
            onClick={onLogout}
            sx={{
              minWidth: 140,
              py: 1.5,
              borderRadius: 2,
              textTransform: "none",
              fontSize: "1rem",
              fontWeight: 600,
            }}
          >
            Login Again
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default SessionTimeoutWarningModal;
